import { useQueryUserPreferences } from '@fabric-app/business/user/queries/useQueryUserPreferences';
import { FabricHaptics } from '@fabric-app/ui/haptics/FabricHaptics';
import { useEffect } from 'react';

const hapticsEnabled =
  typeof window !== 'undefined' && window.matchMedia('(pointer: coarse)').matches;

const hapticTargetSelectors = [
  'a',
  'button',
  'input',
  'textarea',
  'select',
  '[role="link"]',
  '[role="button"]',
  '[role="option"]',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  '[role="radio"]',
].join(',');

/**
 * not invoking the haptic on touchstart, pointerdown etc. because
 * - will trigger when user starts scrolling
 *
 * for this reason we need a confirmation of some sort, might use the onclick BUT
 * - for various radix reasons, often the preceeding events are blocked (stoped, prevented)
 *
 * so we need to simulate the onClick event, and only trigger the haptic on that
 *
 * onTouchStart we set a timestamp
 * onTouchMove we reset the timestamp - assuming user started scrolling
 * onTouchEnd we check if the timestamp is set and if so we trigger the haptic
 *
 *
 */
export const useHapticTouch = () => {
  const { data: preferences } = useQueryUserPreferences();
  const haptics = preferences?.frontend?.hapticFeedback || 'all';

  useEffect(() => {
    FabricHaptics.setHapticsEnabled(haptics);
  }, [haptics]);

  const allEnabled = hapticsEnabled && (haptics === 'all' || haptics === undefined);
  /**
   * Set all haptics
   */
  useEffect(() => {
    if (!allEnabled) {
      return;
    }

    let touchStartTimestamp: number | null = null;

    const handleTouchMove = () => {
      touchStartTimestamp = null;
    };

    const handleTouchEnd = () => {
      if (touchStartTimestamp) {
        touchStartTimestamp = null;

        FabricHaptics.impact({
          style: FabricHaptics.ImpactStyle.Light,
        });
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      const targetElement = 'target' in e ? e.target : null;
      if (!!targetElement && 'closest' in targetElement) {
        const closestElement = (targetElement as HTMLElement).closest(hapticTargetSelectors);
        if (closestElement) {
          touchStartTimestamp = Date.now();
        }
      }
    };

    window.addEventListener('touchmove', handleTouchMove);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchstart', handleTouchStart);

    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchstart', handleTouchStart);
    };
  }, [allEnabled]);
};
